import React, { useState, useRef, useEffect } from "react";
import styles from "./DriverMessagesPage.module.css";
import { DriverMessage } from "./types";
import { mockDriverMessages } from "./mockData";

export default function DriverMessagesPage() {
  const [messages, setMessages] = useState<DriverMessage[]>(mockDriverMessages);
  const [showDrawer, setShowDrawer] = useState(false);
  const [editingMessage, setEditingMessage] = useState<DriverMessage | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const handleCreate = () => {
    setIsCreating(true);
    setEditingMessage({
      id: "",
      status: "Draft",
      title: "",
      content: "",
      createdAt: new Date().toLocaleString(),
      lastUpdated: new Date().toLocaleString(),
      recipientCount: 0
    });
    setShowDrawer(true);
  };

  const handleEdit = (message: DriverMessage) => {
    setIsCreating(false);
    setEditingMessage({ ...message });
    setShowDrawer(true);
    setOpenMenuId(null);
  };

  const handleSend = (message: DriverMessage) => {
    setOpenMenuId(null);
    alert(`Sending message: "${message.title}"`);
  };

  const handleWithdraw = (id: string) => {
    if (window.confirm("Are you sure you want to withdraw this message?")) {
      setMessages(messages.map(msg => 
        msg.id === id ? { ...msg, status: "Draft" as const } : msg
      ));
      setOpenMenuId(null);
    }
  };

  const handleDelete = (id: string) => {
    if (window.confirm("Are you sure you want to delete this message?")) {
      setMessages(messages.filter(msg => msg.id !== id));
      setOpenMenuId(null);
    }
  };

  const handleCloseDrawer = () => {
    setShowDrawer(false);
    setEditingMessage(null);
    setIsCreating(false);
  };

  const handleSave = () => {
    if (!editingMessage) return;

    if (isCreating) {
      const newMessage = {
        ...editingMessage,
        id: Date.now().toString(),
        createdAt: new Date().toLocaleString(),
        lastUpdated: new Date().toLocaleString()
      };
      setMessages([newMessage, ...messages]);
    } else {
      setMessages(messages.map(msg => 
        msg.id === editingMessage.id ? {
          ...editingMessage,
          lastUpdated: new Date().toLocaleString()
        } : msg
      ));
    }

    handleCloseDrawer();
  };

  const toggleMenu = (id: string) => {
    setOpenMenuId(openMenuId === id ? null : id);
  };

  const updateField = (field: keyof DriverMessage, value: any) => {
    if (editingMessage) {
      setEditingMessage({ ...editingMessage, [field]: value });
    }
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpenMenuId(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h1 className={styles.title}>Driver Messages</h1>
        </div>
        <div className={styles.headerRight}>
          <button className={styles.columnsBtn}>Columns</button>
          <button className={styles.resetBtn}>Reset</button>
          <button className={styles.createBtn} onClick={handleCreate}>
            + Create
          </button>
        </div>
      </div>

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>STATUS</th>
              <th>TITLE</th>
              <th>CREATED AT</th>
              <th>LAST UPDATED</th>
              <th>SENT AT</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {messages.map((message) => (
              <tr key={message.id}>
                <td>
                  <span className={`${styles.statusBadge} ${styles[message.status.toLowerCase()]}`}>
                    {message.status}
                  </span>
                </td>
                <td className={styles.messageTitle}>{message.title}</td>
                <td>{message.createdAt}</td>
                <td>{message.lastUpdated}</td>
                <td>{message.sentAt || "-"}</td>
                <td>
                  <div className={styles.actionsCell}>
                    <button 
                      className={styles.menuBtn}
                      onClick={() => toggleMenu(message.id)}
                    >
                      ...
                    </button>
                    {openMenuId === message.id && (
                      <div ref={menuRef} className={styles.dropdown}>
                        <button 
                          className={styles.dropdownItem}
                          onClick={() => handleSend(message)}
                        >
                          Send
                        </button>
                        <button 
                          className={styles.dropdownItem}
                          onClick={() => handleEdit(message)}
                        >
                          Edit
                        </button>
                        {message.status === "Sent" && (
                          <button 
                            className={styles.dropdownItem}
                            onClick={() => handleWithdraw(message.id)}
                          >
                            Withdraw
                          </button>
                        )}
                        <button 
                          className={`${styles.dropdownItem} ${styles.dropdownItemDanger}`}
                          onClick={() => handleDelete(message.id)}
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className={styles.footer}>
        <span className={styles.resultsCount}>
          Showing {messages.length} messages
        </span>
      </div>

      {showDrawer && (
        <>
          <div className={styles.drawerOverlay} onClick={handleCloseDrawer} />
          <div className={styles.drawer}>
            <div className={styles.drawerHeader}>
              <h2 className={styles.drawerTitle}>
                {isCreating ? "New Message" : "Edit Message"}
              </h2>
              <button className={styles.closeBtn} onClick={handleCloseDrawer}>
                &times;
              </button>
            </div>

            <div className={styles.drawerContent}>
              {editingMessage && (
                <form className={styles.form}>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Message Title</label>
                    <input
                      type="text"
                      className={styles.input}
                      placeholder="Enter message title"
                      value={editingMessage.title}
                      onChange={(e) => updateField("title", e.target.value)}
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.label}>Message Content</label>
                    <textarea
                      className={styles.textarea}
                      placeholder="Enter your message content here..."
                      rows={8}
                      value={editingMessage.content || ""}
                      onChange={(e) => updateField("content", e.target.value)}
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.label}>Status</label>
                    <select
                      className={styles.select}
                      value={editingMessage.status}
                      onChange={(e) => updateField("status", e.target.value)}
                    >
                      <option value="Draft">Draft</option>
                      <option value="Scheduled">Scheduled</option>
                      <option value="Sent">Sent</option>
                    </select>
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.label}>Recipient Count</label>
                    <input
                      type="number"
                      className={styles.input}
                      placeholder="Number of recipients"
                      value={editingMessage.recipientCount || 0}
                      onChange={(e) => updateField("recipientCount", parseInt(e.target.value) || 0)}
                    />
                  </div>

                  {editingMessage.status === "Sent" && (
                    <div className={styles.formGroup}>
                      <label className={styles.label}>Sent At</label>
                      <input
                        type="text"
                        className={styles.input}
                        value={editingMessage.sentAt || ""}
                        onChange={(e) => updateField("sentAt", e.target.value)}
                      />
                    </div>
                  )}
                </form>
              )}
            </div>

            <div className={styles.drawerFooter}>
              <button className={styles.cancelBtn} onClick={handleCloseDrawer}>
                Cancel
              </button>
              <button className={styles.saveBtn} onClick={handleSave}>
                {isCreating ? "Create Message" : "Save Changes"}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}